import { useState, useRef } from "react";
import { PasswordInput } from "@/components/common";
import api from "@/lib/api";
import { useAuth, apiError } from "@/context/AuthContext";
import { SectionTitle } from "@/components/common";
import { toast } from "sonner";
import { Loader2, ShieldCheck, User, Mail, Building2, BadgeCheck, Camera, BellRing } from "lucide-react";
import AvatarCropper from "@/components/AvatarCropper";
import { TwoFactorCard } from "@/components/TwoFactor";
import { TrustedDevicesCard } from "@/components/TrustedDevices";
import { InstallAppCard } from "@/components/InstallAppCard";
import { PushNotificationsCard } from "@/components/PushNotificationsCard";
import { WorkingHoursCard } from "@/components/WorkingHoursCard";
import SemaforoConfigCard from "@/components/SemaforoConfigCard";
import DocScadConfigCard from "@/components/DocScadConfigCard";
import SpazioArchivioCard from "@/components/SpazioArchivioCard";
import UltimoBackupRiga from "@/components/UltimoBackupRiga";
import SoglieConfigCard from "@/components/SoglieConfigCard";
import ChecklistTappeConfigCard from "@/components/ChecklistTappeConfigCard";
import { OmiImportCard } from "@/components/OmiCard";
import ValuArtConfigCard from "@/components/ValuArtConfigCard";
import ContestPuntiCard from "@/components/ContestPuntiCard";

const ROLE_LABEL = {
  direzione: "Direzione",
  direttore_commerciale: "Direttore Commerciale",
  agente: "Agente",
  segreteria: "Segreteria",
};

const Field = ({ label, value }) => (
  <div className="flex items-center gap-3 py-2.5 border-b border-[var(--border)] last:border-0">
    <div className="w-8 h-8 rounded-lg bg-[#FC82ED]/12 border border-[#FC82ED]/25 flex items-center justify-center text-[#FC82ED] shrink-0">
      {label.icon}
    </div>
    <div className="min-w-0">
      <div className="text-[11px] uppercase tracking-wider text-[var(--muted)]">{label.text}</div>
      <div className="text-sm font-medium truncate">{value || "—"}</div>
    </div>
  </div>
);

export default function Profilo() {
  const { user, changePassword, canFinance, patchUser, isDirezione } = useAuth();
  const [cur, setCur] = useState("");
  const [pw, setPw] = useState("");
  const [confirm, setConfirm] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);
  const [avatarBusy, setAvatarBusy] = useState(false);
  const [cropFile, setCropFile] = useState(null);
  const fileRef = useRef(null);

  const onAvatar = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!["image/jpeg", "image/png", "image/webp"].includes(f.type)) { toast.error("Formato non valido (JPG, PNG, WEBP)."); return; }
    if (f.size > 5 * 1024 * 1024) { toast.error("Immagine troppo grande (max 5MB)."); return; }
    setCropFile(f);
    if (fileRef.current) fileRef.current.value = "";
  };

  const onCrop = async (blob) => {
    setAvatarBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", blob, "avatar.jpg");
      const { data } = await api.post("/profile/avatar", fd, { headers: { "Content-Type": "multipart/form-data" } });
      patchUser({ avatar: data.avatar });
      toast.success("Foto profilo aggiornata.");
      setCropFile(null);
    } catch (e) { toast.error(apiError(e)); } finally { setAvatarBusy(false); }
  };

  const submit = async (e) => {
    e?.preventDefault();
    setErr("");
    if (!cur) return setErr("Inserisci la password attuale.");
    if (pw.length < 12)
      return setErr("La nuova password deve avere almeno 12 caratteri (con almeno 3 tra minuscole, maiuscole, numeri, simboli).");
    if (pw === cur) return setErr("La nuova password deve essere diversa da quella attuale.");
    if (pw !== confirm) return setErr("Le password non coincidono.");
    setLoading(true);
    try {
      await changePassword(pw, cur);
      toast.success("Password aggiornata con successo.");
      setCur(""); setPw(""); setConfirm("");
    } catch (e) { setErr(apiError(e)); } finally { setLoading(false); }
  };

  const inputCls = "w-full mb-4 px-3.5 py-2.5 rounded-lg bg-white border border-slate-200 focus:border-[#FC82ED] focus:ring-2 focus:ring-[#FC82ED]/20 outline-none text-sm transition-all";
  const labelCls = "block text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-1.5";

  return (
    <div className="fade-up max-w-3xl" data-testid="profilo-page">
      {cropFile && <AvatarCropper file={cropFile} busy={avatarBusy} onCancel={() => setCropFile(null)} onCrop={onCrop} />}
      <SectionTitle sub="Gestisci i tuoi dati di accesso. La modifica della password è personale e riservata al tuo account.">Il mio profilo</SectionTitle>

      <div className="grid md:grid-cols-2 gap-6 mt-6">
        {/* Dati account */}
        <div className="card-immo p-5">
          <div className="flex items-center gap-3 mb-4">
            <button type="button" data-testid="avatar-upload-btn" onClick={() => fileRef.current?.click()}
                    className="relative group shrink-0" title="Cambia foto profilo">
              {user?.avatar ? (
                <img src={user.avatar} alt="" className="w-16 h-16 rounded-full object-cover border border-[var(--border)]" />
              ) : (
                <div className="w-16 h-16 rounded-full bg-[#FC82ED]/15 border border-[#FC82ED]/40 flex items-center justify-center text-[#FC82ED] font-bold text-2xl">
                  {user?.name?.slice(0, 1)}
                </div>
              )}
              <span className="absolute inset-0 rounded-full bg-black/45 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center text-white">
                {avatarBusy ? <Loader2 size={18} className="animate-spin" /> : <Camera size={18} />}
              </span>
              <input ref={fileRef} data-testid="avatar-input" type="file" accept="image/jpeg,image/png,image/webp" onChange={onAvatar} className="hidden" />
            </button>
            <div className="min-w-0">
              <div className="font-display font-bold text-lg truncate" data-testid="profilo-name">{user?.name}</div>
              <div className="text-xs text-[#FC82ED] font-medium">{ROLE_LABEL[user?.role] || user?.role}</div>
              <div className="text-[11px] text-[var(--muted)] mt-0.5">Clicca la foto per cambiarla (JPG/PNG/WEBP, max 5MB)</div>
            </div>
          </div>
          <Field label={{ icon: <User size={15} />, text: "Nome" }} value={user?.name} />
          <Field label={{ icon: <Mail size={15} />, text: "Email" }} value={user?.email} />
          <Field label={{ icon: <BadgeCheck size={15} />, text: "Ruolo" }} value={ROLE_LABEL[user?.role] || user?.role} />
          <Field label={{ icon: <Building2 size={15} />, text: "Team" }} value={user?.team_name || (user?.role === "direzione" ? "Azienda" : "—")} />
          {canFinance && (
            <div className="mt-3 inline-flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-emerald-600 bg-emerald-400/10 border border-emerald-400/30 rounded-full px-2.5 py-1">
              <ShieldCheck size={13} /> Finanza autorizzata
            </div>
          )}
        </div>

        {/* Cambia password */}
        <div className="card-immo p-5">
          <div className="flex items-center gap-2.5 mb-4">
            <div className="w-9 h-9 rounded-xl bg-[#FC82ED]/15 border border-[#FC82ED]/30 flex items-center justify-center text-[#FC82ED]">
              <ShieldCheck size={18} />
            </div>
            <h3 className="font-display font-bold text-base">Cambia password</h3>
          </div>

          <form onSubmit={submit}>
            <label className={labelCls}>Password attuale</label>
            <PasswordInput testid="profile-current-pw" value={cur} onChange={(e) => setCur(e.target.value)}
                   autoComplete="current-password" className={inputCls} placeholder="••••••••" />

            <label className={labelCls}>Nuova password</label>
            <PasswordInput testid="profile-new-pw" value={pw} onChange={(e) => setPw(e.target.value)}
                   autoComplete="new-password" className={inputCls} placeholder="Almeno 12 caratteri, mix di tipi" />

            <label className={labelCls}>Conferma nuova password</label>
            <PasswordInput testid="profile-confirm-pw" value={confirm} onChange={(e) => setConfirm(e.target.value)}
                   autoComplete="new-password" className={inputCls} placeholder="••••••••" />

            {err && <div data-testid="profile-pw-error" className="mb-4 text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{err}</div>}

            <button data-testid="profile-save-btn" type="submit" disabled={loading}
                    className="w-full py-3 rounded-lg bg-[#FC82ED] hover:bg-[#e06dd2] text-black font-semibold text-sm transition-colors flex items-center justify-center gap-2 disabled:opacity-60">
              {loading && <Loader2 size={16} className="animate-spin" />} Salva nuova password
            </button>
            <p className="text-[11px] text-[var(--muted)] mt-3 leading-relaxed">Per la tua sicurezza serve la password attuale. Dopo il salvataggio resti collegato con la nuova password.</p>
          </form>
        </div>
      </div>

      <div className="mt-6">
        <TwoFactorCard />
      </div>

      <div className="mt-6">
        <ReminderPrefCard />
      </div>

      {canFinance && (
        <div className="mt-6">
          <FinanceDigestCard />
        </div>
      )}

      <div className="mt-6">
        <WorkingHoursCard />
        {isDirezione && <SemaforoConfigCard />}
        {isDirezione && <UltimoBackupRiga />}
        {isDirezione && <DocScadConfigCard />}
        {isDirezione && <SpazioArchivioCard />}
        {isDirezione && <SoglieConfigCard />}
        {isDirezione && <ChecklistTappeConfigCard />}
        {isDirezione && <OmiImportCard />}
        {isDirezione && <ValuArtConfigCard />}
        {isDirezione && <ContestPuntiCard />}
      </div>

      <div className="mt-6">
        <InstallAppCard />
      </div>

      <div className="mt-6">
        <PushNotificationsCard />
      </div>

      <div className="mt-6">
        <TrustedDevicesCard />
      </div>
    </div>
  );
}



function FinanceDigestCard() {
  const { user, patchUser } = useAuth();
  const [on, setOn] = useState(user?.finance_digest_enabled !== false);
  const [saving, setSaving] = useState(false);
  const toggle = async () => {
    const next = !on;
    setOn(next); setSaving(true);
    try {
      await api.put("/profile/preferences", { finance_digest_enabled: next });
      patchUser({ finance_digest_enabled: next });
      toast.success(next ? "Riepilogo finanza giornaliero attivato." : "Riepilogo finanza giornaliero disattivato.");
    } catch (e) { setOn(!next); toast.error(apiError(e)); } finally { setSaving(false); }
  };
  return (
    <div className="card-immo p-5" data-testid="finance-digest-card">
      <div className="flex items-center gap-2.5 mb-3">
        <div className="w-9 h-9 rounded-xl bg-[#FC82ED]/15 border border-[#FC82ED]/30 flex items-center justify-center text-[#FC82ED]"><Mail size={18} /></div>
        <h3 className="font-display font-bold text-base">Riepilogo finanza giornaliero</h3>
      </div>
      <p className="text-sm text-[var(--muted)] mb-3">Un'unica email ogni mattina alle 07:45 con debiti e incassi in scadenza, categorie oltre il tetto e, il lunedì, la previsione settimanale. Nessun'altra email finanziaria automatica viene inviata.</p>
      <button type="button" role="switch" aria-checked={on} disabled={saving} onClick={toggle} data-testid="finance-digest-toggle"
              className={`relative inline-flex items-center h-7 w-12 rounded-full transition-colors disabled:opacity-60 ${on ? "bg-[#FC82ED]" : "bg-slate-300"}`}>
        <span className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${on ? "translate-x-6" : "translate-x-1"}`} />
      </button>
      <span className="ml-3 text-sm font-medium align-middle">{on ? "Attivo" : "Disattivato"}</span>
    </div>
  );
}


function ReminderPrefCard() {
  const { user, patchUser } = useAuth();
  const [val, setVal] = useState(String(user?.reminder_minuti || 60));
  const [val2, setVal2] = useState(String(user?.reminder2_minuti || 0));
  const [saving, setSaving] = useState(false);
  const save = async (v) => {
    setVal(v); setSaving(true);
    try {
      await api.put("/profile/preferences", { reminder_minuti: parseInt(v) });
      patchUser({ reminder_minuti: parseInt(v) });
      toast.success("Promemoria aggiornato.");
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };
  const save2 = async (v) => {
    setVal2(v); setSaving(true);
    try {
      await api.put("/profile/preferences", { reminder2_minuti: parseInt(v) });
      patchUser({ reminder2_minuti: parseInt(v) });
      toast.success("Secondo promemoria aggiornato.");
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };
  return (
    <div className="card-immo p-5" data-testid="reminder-pref-card">
      <div className="flex items-center gap-2.5 mb-3">
        <div className="w-9 h-9 rounded-xl bg-[#FC82ED]/15 border border-[#FC82ED]/30 flex items-center justify-center text-[#FC82ED]"><BellRing size={18} /></div>
        <h3 className="font-display font-bold text-base">Promemoria appuntamenti</h3>
      </div>
      <p className="text-sm text-[var(--muted)] mb-3">Quando ricevere la notifica (in-app e push) prima di un appuntamento in agenda. Puoi impostare due promemoria.</p>
      <label className="block text-[11px] font-semibold uppercase tracking-wide text-[var(--muted)] mb-1">Primo promemoria</label>
      <select data-testid="reminder-minuti-select" value={val} onChange={(e) => save(e.target.value)} disabled={saving}
              className="w-full px-3 py-2.5 rounded-lg bg-white border border-slate-200 focus:border-[#FC82ED] outline-none text-sm mb-3">
        <option value="15">15 minuti prima</option>
        <option value="30">30 minuti prima</option>
        <option value="60">1 ora prima</option>
        <option value="120">2 ore prima</option>
      </select>
      <label className="block text-[11px] font-semibold uppercase tracking-wide text-[var(--muted)] mb-1">Secondo promemoria (facoltativo)</label>
      <select data-testid="reminder2-minuti-select" value={val2} onChange={(e) => save2(e.target.value)} disabled={saving}
              className="w-full px-3 py-2.5 rounded-lg bg-white border border-slate-200 focus:border-[#FC82ED] outline-none text-sm">
        <option value="0">Nessuno</option>
        <option value="120">2 ore prima</option>
        <option value="360">6 ore prima</option>
        <option value="720">12 ore prima</option>
        <option value="1440">24 ore prima</option>
        <option value="2880">2 giorni prima</option>
      </select>
    </div>
  );
}
